import { Component, ElementRef, ViewChild } from '@angular/core';
import {
  grades,
  UserPermissionsModel,
} from '../../../../Models/ApplicationModels';
import { MasterDataSeriveService } from '../../../../Services/master-data-serive.service';
import { CustomToastsService } from '../../../../Services/custom-toasts.service';
import { ExportExcelService } from '../../../../Services/export-excel.service';
import { AdminBreadcrumbsService } from '../../admin-services/admin-breadcrumbs.service';
import { NgxPermissionsService } from 'ngx-permissions';
import { UserserviceService } from 'src/app/Services/userservice.service';
import { ImportServiceService } from 'src/app/Services/import-service.service';
import * as XLSX from 'xlsx';
import FileSaver from 'file-saver';

@Component({
  selector: 'app-quality',
  templateUrl: './quality.component.html',
  styleUrls: ['./quality.component.css'],
})
export class QualityComponent {
  // Core data
  gradesarray: Array<grades> = [];
  grade: grades = new grades();
  exportList: any = [];

  // UI state
  headerCheckboxSelected: boolean = false;
  selectedGradeIDs: string[] = [];
  selectedRowIndex: number | null = null;
  frmQtyText: string = 'Add New Quality';
  hideUpdateBtn: boolean = true;
  isUpdated = false;

  // Delete modal state
  deleteParameter: any = null;
  deleteButtonClickedWithoutSelection: boolean = false;
  deleteButtonClickedWithSelection: boolean = false;

  // Import/Export state
  exportColumnsArr = [['Quality', 'Quality Code', 'Quality Color']];
  importQualityList: any[] = [];
  isImportDisabled: boolean = true;
  toggleAll: boolean = false;
  isInvalidImport: boolean = false;

  @ViewChild('modalConfirmCloseButton')
  modalConfirmCloseButton?: ElementRef<HTMLElement>;
  @ViewChild('importQuality') importQuality!: ElementRef<HTMLButtonElement>;
  @ViewChild('importModel') importModel!: ElementRef<HTMLButtonElement>;

  constructor(
    private permissionService: NgxPermissionsService,
    private userservice: UserserviceService,
    private masterservice: MasterDataSeriveService,
    private toastr: CustomToastsService,
    private adminBreadcrumbs: AdminBreadcrumbsService,
    private exportExcelService: ExportExcelService,
    private importService: ImportServiceService
  ) { }

  ngOnInit() {
    this.adminBreadcrumbs.updateBreadcrumbs([
      { crumbName: 'Company Config', link: 'demo' },
      { crumbName: 'Quality & Conditions', link: '/admin/home/grades' },
    ]);
    this.getGrades();
    this.getPermissionsByUserId();
  }

  // Toast notification helpers
  private showSuccess(): void {
    this.toastr.success('New Quality has been successfully added.');
  }

  private showUpdated(): void {
    this.toastr.success('Quality has been successfully updated.');
  }

  private showError(): void {
    this.toastr.error('Quality Already Exist');
  }

  highlightedRow(id: any): void {
    this.selectedRowIndex = id ? Number(id) : null;
  }

  // Data fetching and preparation
  getGrades(): void {
    const userdata: any = this.masterservice.getUserDetailsFromStorage();
    const companyId = userdata.companyID;

    this.masterservice.getGrades(companyId).subscribe((res) => {
      if (res) {
        this.gradesarray = res as grades[];
        this.normalizeQualityColors();
        this.prepareExportList();
      }
    });
  }

  private normalizeQualityColors(): void {
    this.gradesarray.forEach((item) => {
      item.qualityColor = item.qualityColor.replace(/\s/g, '').toLowerCase();
    });
  }

  private prepareExportList(): void {
    this.exportList = this.gradesarray.map((qlty) => ({
      Quality: qlty.qualityDescr,
      Quality_Code: qlty.qualityCode,
      Quality_Color: qlty.qualityColor,
      Date_Created: this.formatDateForExport(qlty.createdDt),
      Created_By: qlty.userName,
    }));
  }

  private formatDateForExport(dateString: string): string {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}-${day}-${year}`;
  }

  exportToExcel(): void {
    this.exportExcelService.exportAsExcelFile(this.exportList, 'Quality-data');
  }

  // Form management
  clearForm(): void {
    this.hideUpdateBtn = true;
    this.frmQtyText = 'Add New Quality';
    this.grade = new grades();
    this.isUpdated = false;
  }

  creategrade(data: grades): void {
    const userdata: any = this.masterservice.getUserDetailsFromStorage();
    data.createdByUserId = userdata.userId;
    data.lastChangedByUserId = userdata.userId;
    data.companyId = userdata.companyID;

    this.masterservice.addupdategrade(data).subscribe(
      (res) => {
        if (res) {
          this.getGrades();
          this.isUpdated ? this.showUpdated() : this.showSuccess();
          this.isUpdated = false;
        }
      },
      (err) => {
        this.showError();
      }
    );
  }

  getGrade(grade: grades): void {
    this.hideUpdateBtn = false;
    this.frmQtyText = 'Update Quality';
    this.isUpdated = true;
    this.grade = { ...grade }; // Create a copy to avoid direct mutation
  }

  // Delete operations
  openConfirmPopup(quality: any): void {
    let qualityId: any;

    if (Array.isArray(quality)) {
      qualityId = quality.length > 0 ? quality.map(id => Number(id)) : null;
    } else if (quality && quality.qualityId) {
      qualityId = quality.qualityId;
    } else {
      qualityId = null;
    }

    const hasSelection = qualityId !== null && !(Array.isArray(qualityId) && qualityId.length === 0);

    this.deleteButtonClickedWithSelection = hasSelection;
    this.deleteButtonClickedWithoutSelection = !hasSelection;
    this.deleteParameter = hasSelection ? qualityId : null;
  }

  isSelected(grade: grades): boolean {
    return this.selectedGradeIDs.includes(grade.qualityId.toString());
  }

  // Checkbox selection management
  handleCheckboxChange(event: any, grade: grades): void {
    const qualityIdStr = grade.qualityId.toString();

    if (event.target.checked) {
      this.selectedGradeIDs.push(qualityIdStr);
      grade.isSelected = true;
    } else {
      const index = this.selectedGradeIDs.indexOf(qualityIdStr);
      if (index !== -1) {
        this.selectedGradeIDs.splice(index, 1);
      }
      grade.isSelected = false;
      this.headerCheckboxSelected = false;
    }

    // Update header checkbox if all items are selected
    this.headerCheckboxSelected = this.selectedGradeIDs.length === this.gradesarray.length;
  }

  private uncheckSelectedCheckboxes(): void {
    this.selectedGradeIDs = [];
    this.headerCheckboxSelected = false;
    this.gradesarray.forEach(grade => grade.isSelected = false);
  }

  toggleAllCheckboxes(): void {
    this.headerCheckboxSelected = !this.headerCheckboxSelected;

    if (this.headerCheckboxSelected) {
      this.selectedGradeIDs = this.gradesarray.map(grade => grade.qualityId.toString());
      this.gradesarray.forEach(grade => grade.isSelected = true);
    } else {
      this.uncheckSelectedCheckboxes();
    }
  }

  private closeModal(): void {
    this.modalConfirmCloseButton?.nativeElement.click();
  }

  deleteGrade(deleteParameter: number | number[]): void {
    const idsArray = Array.isArray(deleteParameter) ? deleteParameter : [deleteParameter];
    const selectedIds = idsArray.join(',');

    this.masterservice.deleteGrade(selectedIds).subscribe((res) => {
      if (res) {
        this.toastr.success('Quality has been successfully deleted.');
        this.getGrades();
        this.uncheckSelectedCheckboxes();
        this.closeModal();
      }
    });
  }

  // Permissions
  private getPermissionsByUserId(): void {
    this.userservice.getUserRolesandPerm().then((res) => {
      const permissions = res as UserPermissionsModel[];
      const permissionNames = permissions?.map(pr => pr.name) || [];
      this.permissionService.loadPermissions(permissionNames);
    });
  }

  // Import/Export operations
  exportQualityTemplate(): void {
    const ws: XLSX.WorkSheet = XLSX.utils.aoa_to_sheet(this.exportColumnsArr);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Quality');
    const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    FileSaver.saveAs(new Blob([wbout]), 'Quality.xlsx');
  }
  importQualityColor(): void {
    this.importQuality?.nativeElement.click();
  }

  onFileChangeImport(event: any): void {
    const user = this.masterservice.getUserDetailsFromStorage();
    const file = event.target.files[0];

    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const workbook = XLSX.read(e.target.result, { type: 'binary' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const rawData: any[][] = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      const filteredData = rawData.filter(row =>
        row.some(cell => cell !== null && cell !== undefined && cell !== '')
      );

      this.importQualityList = this.transformToJsonArray(filteredData);
      this.initializeImportList(user);
      this.importModel?.nativeElement.click();
    };

    reader.readAsBinaryString(file);
  }

  private initializeImportList(user: any): void {
    this.importQualityList.forEach(item => {
      item.createdByUserID = user.userId;
      item.companyID = user.companyID;
      item.outletID = user.storeId;
      item.isChecked = false;
      item.addStatus = 2;
      item.status = null;
    });
  }
  private transformToJsonArray(data: any[][]): any[] {
    if (data.length === 0) return [];

    const headerMapping: { [key: string]: string } = {
      'Quality': 'qualityDescr',
      'Quality Code': 'qualityCode',
      'Quality Color': 'qualityColor',
    };

    const headers = data[0];
    const camelCaseHeaders = headers.map(header =>
      headerMapping[header] || header.toLowerCase().replace(/\s+/g, '')
    );

    return data.slice(1).map(row => {
      const obj: any = {};
      camelCaseHeaders.forEach((header, index) => {
        obj[header] = row[index];
      });
      return obj;
    });
  }
  isImportEnabled(): void {
    const hasCheckedItems = this.importQualityList.some(item => item.isChecked);
    this.isImportDisabled = !hasCheckedItems;
  }
  closeImportModal(): void {
    // Reset file input to allow re-selection of the same file
    if (this.importQuality) {
      this.importQuality.nativeElement.value = '';
    }
  }

  checkAllImport(event: Event): void {
    const isChecked = (event.target as HTMLInputElement).checked;
    this.toggleAll = isChecked;

    this.importQualityList.forEach(item => {
      if (item.addStatus === 2) {
        item.isChecked = isChecked;
      }
    });

    this.isImportEnabled();
  }
  addImportedQuality(): void {
    const userinfo = this.masterservice.getUserDetailsFromStorage();
    const qualityList = this.importQualityList
      .filter(item => item.isChecked)
      .map(({ status, ...rest }) => ({
        ...rest,
        qualityDescr: String(rest.qualityDescr ?? ''),
        qualityCode: String(rest.qualityCode ?? ''),
        qualityColor: String(rest.qualityColor ?? '')
      }));


    if (qualityList.length === 0) {
      this.toastr.error('Invalid Selection');
      return;
    }

    // Validate entries
    const hasInvalidEntry = qualityList.some(quality =>
      !quality.qualityDescr && !quality.qualityCode
    );

    if (hasInvalidEntry) {
      this.toastr.error('Invalid entry in import file');
      return;
    }

    // Prepare quality list for import
    qualityList.forEach(quality => {
      quality.active = true;
      quality.companyID = userinfo.companyID;
      quality.createdByUserID = userinfo.userId;
    });

    this.importService.ImportQuality(qualityList).subscribe((res) => {
      this.importQualityList = res as any[];
      this.processImportResponse();
      this.isImportDisabled = true;
      this.toggleAll = false;
      this.getGrades();
    });
  }

  private processImportResponse(): void {
    this.importQualityList.forEach(item => {
      if (item.reason != null) {
        item.reason = item.reason
          .split(',')
          .map((reason: string) => reason.trim())
          .filter((reason: string) => reason.length > 0);
      }
    });
  }

  getRemainingErrors(reasonString: string): string {
    const errors = reasonString.split('|');
    return errors.slice(1).join('\n');
  }
}
